import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';
import { useActionLogSender } from '@framework/action-log';
import { usePrevious } from '@view-model/models/common/hooks/usePrevious';

type Props = {
  viewModel: ViewModelEntity;
  isOpen: boolean;
  onClose(): void;
};

export const ViewModelDeleteConfirmModal: React.FC<Props> = ({ viewModel, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const previousIsOpen = usePrevious(isOpen);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const actionLogSender = useActionLogSender();

  useEffect(() => {
    if (isOpen && !previousIsOpen) {
      // モーダルを開いた時に、各stateを初期状態に戻す
      setConfirmed(false);
      setIsSubmitting(false);
    }
  }, [isOpen, previousIsOpen]);

  const handleSubmit = useCallback(() => {
    if (!confirmed) return;

    setIsSubmitting(true);

    ViewModelOperation.delete(viewModel.id, actionLogSender)
      .then(() => {
        onClose();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [actionLogSender, confirmed, onClose, viewModel.id]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('canvas.viewModelDelete.confirm')}
      submitButton={
        <Button color="danger" onClick={handleSubmit} disabled={!confirmed} loading={isSubmitting}>
          {t('common.action.delete')}
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div>
        <p>
          <Trans i18nKey="canvas.viewModelDelete.nameConfirm" values={{ name: viewModel.name.value }} />
        </p>
        <p className="py-4">
          {t('canvas.viewModelDelete.warningViews')}
          <br />
          {t('canvas.viewModelDelete.warningUndo')}
        </p>
        <label className="block">
          <input
            type="checkbox"
            className="form-checkbox mr-1 size-6 border-brand"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          {t('canvas.viewModelDelete.checkboxLabel')}
        </label>
      </div>
    </GeneralModal>
  );
};
